import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Menu, X, ChevronDown } from "lucide-react";
import img1 from '../Images/gorthermlogo.png';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeSubDropdown, setActiveSubDropdown] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth >= 1024) {
        setIsOpen(false);
        setActiveDropdown(null);
        setActiveSubDropdown(null);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isOpen && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isMobile]);

  const products = [
    { name: "Process Heating Solutions", href: "/products/process-heating" },
    { name: "Water & Waste Management", href: "/products/water-management" },
    { name: "Power Generation Systems", href: "/products/power-generation" },
    { name: "Pollution Control Equipment", href: "/products/pollution-control" },
    { name: "Industrial Chemicals", href: "/products/chemicals" },
    { name: "HVAC Systems", href: "/products/hvac" },
    { name: "Solar Energy Solutions", href: "/products/solar" },
    { name: "Steam Accessories", href: "/products/steam-accessories" },
  ];
  
  const services = [
    {
      name: "Process Energy Services",
      href: "#",
      subItems: [
        { name: "Piping Design & Consultation", href: "/services/consulting/pipingdesign" },
        { name: "Energy Audit Services", href: "/services/consulting/energyaudit" },
      ],
    },
    {
      name: "Retrofit & Revamp",
      href: "#",
      subItems: [
        { name: "Boiler Automation", href: "/services/implementation/boilerautomation" },
        { name: "Solid Fuel Conversion", href: "/services/implementation/solidfuelconversion" },
        { name: "System Upgrades", href: "/services/implementation/upgrades" },
      ],
    },
    {
      name: "Specialized Solutions",
      href: "#",
      subItems: [
        { name: "Maintenance & Overhauling", href: "/services/maintenance/maintenanceservice" },
        { name: "Equipment Health Checkup", href: "/services/maintenance/healthcheckup" },
        { name: "Descaling Services", href: "/services/maintenance/descaling" },
        { name: "Boiler Operations", href: "/services/maintenance/operationboiler" },
      ],
    },
    {
      name: "Spare Parts",
      href: "#",
      subItems: [
        { name: "Boiler Spares", href: "/services/spares/boiler" },
        { name: "Water Treatment Spares", href: "/services/spares/w&wspare" },
        { name: "Swimming Pool Spares", href: "/services/spares/swimmingpool" },
        { name: "Cooling Water Spares", href: "/services/spares/coolingwater" },
      ],
    },
  ];

  const handleMobileMenuClose = () => {
    setIsOpen(false);
    setActiveDropdown(null);
    setActiveSubDropdown(null);
  };

  const toggleDropdown = (dropdownName) => {
    if (activeDropdown === dropdownName) {
      setActiveDropdown(null);
      setActiveSubDropdown(null);
    } else {
      setActiveDropdown(dropdownName);
      setActiveSubDropdown(null);
    }
  };

  const toggleSubDropdown = (subDropdownName) => {
    setActiveSubDropdown(activeSubDropdown === subDropdownName ? null : subDropdownName);
  };

  // Navigation Schema for SEO
  const navigationSchema = {
    "@context": "https://schema.org",
    "@type": "SiteNavigationElement",
    "name": "Main Navigation",
    "url": "https://www.gorthermenergy.com",
    "hasPart": [
      {
        "@type": "WebPage",
        "name": "Home",
        "url": "https://www.gorthermenergy.com/"
      },
      {
        "@type": "WebPage",
        "name": "Products & Solutions",
        "url": "https://www.gorthermenergy.com/products"
      },
      {
        "@type": "WebPage",
        "name": "Services",
        "url": "https://www.gorthermenergy.com/services"
      },
      {
        "@type": "WebPage",
        "name": "About Us",
        "url": "https://www.gorthermenergy.com/about"
      },
      {
        "@type": "WebPage",
        "name": "Contact",
        "url": "https://www.gorthermenergy.com/contact"
      }
    ]
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(navigationSchema, null, 2)}
        </script>
      </Helmet>

      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 
        ${scrolled ? "bg-sky-700 shadow-lg" : "bg-gradient-to-r from-sky-800 to-sky-600"}`}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="flex justify-between h-16 sm:h-20 md:h-24">
            
            {/* Logo and Company Name */}
            <div className="flex-shrink-0 flex items-center space-x-1 sm:space-x-2">
              <a href="/" className="relative group flex items-center space-x-1 sm:space-x-2" aria-label="Gortherm Energy Home">
                <div className="relative">
                  <img 
                    src={img1} 
                    alt="Gortherm Energy - Industrial Boiler Solutions Nepal" 
                    className="h-12 w-12 sm:h-16 sm:w-16 md:h-20 md:w-20 lg:h-24 lg:w-24 object-contain group-hover:scale-105 transition-transform duration-300" 
                    width="96"
                    height="96"
                  />
                  <div className="absolute inset-0 bg-white/20 rounded-full scale-0 group-hover:scale-100 transition-transform duration-500 blur-xl" aria-hidden="true"/>
                </div>
                <div className="relative hidden sm:block">
                  <span className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-orange-700 relative inline-block transition-all duration-300 group-hover:text-red-900 whitespace-nowrap">
                    Gortherm Energy
                  </span>
                  <div className="absolute inset-0 bg-red-900/20 opacity-0 group-hover:opacity-100 blur-xl transition-all duration-300" aria-hidden="true"/>
                </div>
              </a>
            </div>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center space-x-4 xl:space-x-6">
              <a href="/" className="text-white px-2 xl:px-3 py-2 text-sm xl:text-base font-medium hover:text-red-500 transition-colors duration-300">
                Home
              </a>


                            {/* Services Dropdown */}
              <div 
                className="relative group h-full flex items-center"
                onMouseEnter={() => !isMobile && setActiveDropdown("services")}
                onMouseLeave={() => !isMobile && setActiveDropdown(null)}
              >
                <button 
                  className="text-white group px-2 xl:px-3 py-2 text-sm xl:text-base font-medium hover:text-red-500 inline-flex items-center transition-colors duration-300"
                  aria-expanded={activeDropdown === "services"}
                  aria-haspopup="true"
                >
                  Services
                  <ChevronDown className="ml-1 h-4 w-4 xl:h-5 xl:w-5 transform group-hover:rotate-180 transition-transform duration-300" aria-hidden="true" />
                </button>

                <div 
                  className={`absolute w-56 xl:w-64 rounded-md shadow-xl bg-white ring-1 ring-black ring-opacity-5 transform transition-all duration-300
                  ${activeDropdown === "services" ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2"}`}
                  style={{ top: '100%' }}
                  role="menu"
                >
                  <div className="py-1">
                    {services.map((service, index) => (
                      <div 
                        key={service.name} 
                        className="relative group/sub"
                        onMouseEnter={() => !isMobile && setActiveSubDropdown(service.name)}
                        onMouseLeave={() => !isMobile && setActiveSubDropdown(null)}
                      >
                        <button
                          style={{ transitionDelay: `${index * 50}ms` }}
                          className="w-full px-4 py-2 text-sm xl:text-base text-gray-700 hover:bg-red-50 hover:text-red-500 flex justify-between items-center transform hover:translate-x-2 transition-all duration-300 text-left"
                          aria-expanded={activeSubDropdown === service.name}
                          aria-haspopup="true"
                        >
                          {service.name}
                          <ChevronDown className="h-4 w-4 xl:h-5 xl:w-5 -rotate-90 transition-transform duration-300" aria-hidden="true" />
                        </button>

                        <div 
                          className={`absolute left-full top-0 w-52 xl:w-56 rounded-md shadow-xl bg-white ring-1 ring-black ring-opacity-5 transform transition-all duration-300 
                          ${activeSubDropdown === service.name ? "opacity-100 visible translate-x-0" : "opacity-0 invisible -translate-x-2"}`}
                          role="menu"
                        >
                          <div className="py-1">
                            {service.subItems.map((subItem, subIndex) => (
                              <a 
                                key={subItem.name} 
                                href={subItem.href}
                                style={{ transitionDelay: `${subIndex * 50}ms` }}
                                className="block px-4 py-2 text-sm xl:text-base text-gray-700 hover:bg-red-50 hover:text-red-500 transform hover:translate-x-2 transition-all duration-300"
                                role="menuitem"
                              >
                                {subItem.name}
                              </a>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Products Dropdown */}
              <div 
                className="relative group h-full flex items-center"
                onMouseEnter={() => !isMobile && setActiveDropdown("products")}
                onMouseLeave={() => !isMobile && setActiveDropdown(null)}
              >
                <button 
                  className="text-white group px-2 xl:px-3 py-2 text-sm xl:text-base font-medium hover:text-red-500 inline-flex items-center transition-colors duration-300"
                  aria-expanded={activeDropdown === "products"}
                  aria-haspopup="true"
                >
                  Products & Solutions
                  <ChevronDown className="ml-1 h-4 w-4 xl:h-5 xl:w-5 transform group-hover:rotate-180 transition-transform duration-300" aria-hidden="true" />
                </button>

                <div 
                  className={`absolute w-56 xl:w-64 rounded-md shadow-xl bg-white ring-1 ring-black ring-opacity-5 transform transition-all duration-300
                  ${activeDropdown === "products" ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2"}`}
                  style={{ top: '100%' }}
                  role="menu"
                >
                  <div className="py-1">
                    {products.map((product, index) => (
                      <a 
                        key={product.name} 
                        href={product.href}
                        style={{ transitionDelay: `${index * 50}ms` }}
                        className="block px-4 py-2 text-sm xl:text-base text-gray-700 hover:bg-red-50 hover:text-red-500 transform hover:translate-x-2 transition-all duration-300"
                        role="menuitem"
                      >
                        {product.name}
                      </a>
                    ))}
                  </div>
                </div>
              </div>


              <a href="/about" className="text-white px-2 xl:px-3 py-2 text-sm xl:text-base font-medium hover:text-red-500 transition-colors duration-300">
                About Us
              </a>

              <a href="/contact" className="text-white px-2 xl:px-3 py-2 text-sm xl:text-base font-medium hover:text-red-500 transition-colors duration-300">
                Contact
              </a>
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden flex items-center">
              <button 
                onClick={() => setIsOpen(!isOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-white hover:text-red-500 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-500 transition-all duration-300"
                aria-expanded={isOpen}
                aria-label="Toggle navigation menu"
              >
                {isOpen ? (
                  <X className="h-6 w-6 sm:h-7 sm:w-7 transform rotate-90 transition-transform duration-300" />
                ) : (
                  <Menu className="h-6 w-6 sm:h-7 sm:w-7 transform rotate-0 transition-transform duration-300" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div 
          className={`lg:hidden fixed inset-x-0 bg-sky-700 shadow-lg transition-all duration-300 ease-in-out ${
            isOpen ? "top-16 sm:top-20 bottom-0 opacity-100" : "top-16 sm:top-20 opacity-0 pointer-events-none"
          }`}
          style={{ 
            maxHeight: isOpen ? 'calc(100vh - 4rem)' : '0',
            overflowY: isOpen ? 'auto' : 'hidden'
          }}
        >
          <div className="px-2 pt-2 pb-3 space-y-1">
            <a 
              href="/" 
              className="block px-3 py-3 rounded-md text-base font-medium text-white hover:text-red-500 hover:bg-white/20 transition-all duration-300"
              onClick={handleMobileMenuClose}
            >
              Home
            </a>

                        <div className="relative">
              <button 
                onClick={() => toggleDropdown("services")}
                className="w-full px-3 py-3 text-base font-medium text-white hover:text-red-500 hover:bg-white/20 flex justify-between items-center transition-all duration-300 rounded-md"
                aria-expanded={activeDropdown === "services"}
              >
                Services
                <ChevronDown 
                  className={`h-5 w-5 transform transition-transform duration-300 ${
                    activeDropdown === "services" ? "rotate-180" : "rotate-0"
                  }`}
                  aria-hidden="true"
                />
              </button>
              <div 
                style={{
                  maxHeight: activeDropdown === "services" ? '1000px' : '0',
                  overflow: 'hidden',
                  transition: 'max-height 0.3s ease-in-out'
                }}
              >
                <div className="bg-sky-800/50 rounded-md mt-1">
                  {services.map((service) => (
                    <div key={service.name}>
                      <button 
                        onClick={() => toggleSubDropdown(service.name)}
                        className="w-full px-6 py-2.5 text-sm text-white hover:text-red-500 hover:bg-white/20 flex justify-between items-center transition-all duration-300"
                        aria-expanded={activeSubDropdown === service.name}
                      >
                        {service.name}
                        <ChevronDown 
                          className={`h-4 w-4 transform transition-transform duration-300 ${
                            activeSubDropdown === service.name ? "rotate-180" : "rotate-0"
                          }`}
                          aria-hidden="true"
                        />
                      </button>
                      <div 
                        style={{
                          maxHeight: activeSubDropdown === service.name ? '300px' : '0',
                          overflow: 'hidden',
                          transition: 'max-height 0.3s ease-in-out'
                        }}
                      >
                        <div className="bg-sky-900/50 rounded-md my-1 mx-3">
                          {service.subItems.map((subItem) => (
                            <a 
                              key={subItem.name} 
                              href={subItem.href}
                              className="block px-6 py-2 text-xs text-white hover:text-red-500 hover:bg-white/20 transition-all duration-300"
                              onClick={handleMobileMenuClose}
                            >
                              {subItem.name}
                            </a>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>


            {/* Mobile Products Dropdown */}
            <div className="relative">
              <button 
                onClick={() => toggleDropdown("products")}
                className="w-full px-3 py-3 text-base font-medium text-white hover:text-red-500 hover:bg-white/20 flex justify-between items-center transition-all duration-300 rounded-md"
                aria-expanded={activeDropdown === "products"}
              >
                Products & Solutions
                <ChevronDown 
                  className={`h-5 w-5 transform transition-transform duration-300 ${
                    activeDropdown === "products" ? "rotate-180" : "rotate-0"
                  }`} 
                  aria-hidden="true"
                />
              </button>
              <div 
                style={{
                  maxHeight: activeDropdown === "products" ? '500px' : '0',
                  overflow: 'hidden',
                  transition: 'max-height 0.3s ease-in-out'
                }}
              >
                <div className="bg-sky-800/50 rounded-md mt-1">
                  {products.map((product) => (
                    <a 
                      key={product.name} 
                      href={product.href}
                      className="block px-6 py-2.5 text-sm text-white hover:text-red-500 hover:bg-white/20 transition-all duration-300"
                      onClick={handleMobileMenuClose}
                    >
                      {product.name}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            
            <a 
              href="/about" 
              className="block px-3 py-3 rounded-md text-base font-medium text-white hover:text-red-500 hover:bg-white/20 transition-all duration-300"
              onClick={handleMobileMenuClose}
            >
              About Us
            </a>

            <a 
              href="/contact" 
              className="block px-3 py-3 rounded-md text-base font-medium text-white hover:text-red-500 hover:bg-white/20 transition-all duration-300"
              onClick={handleMobileMenuClose}
            >
              Contact
            </a>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;